
import * as THREE from "../../libs/three.js/build/three.module.js";
import {Utils} from "../utils.js";
import {shouldIgnoreGlobalShortcut} from "../utils/keyboardGuard.js";
import {computeResidentOctreeExtent, computeTopFramingPlacement} from "./topFramingMath.js";

// PointView-specific "T" top-view shortcut - a SEPARATE function from
// Potree's own Top View (viewer.setTopView()/fitToScreen()/zoomTo()), which
// this file never calls or modifies (task: "Den eksisterende Potree Top
// View må IKKE ændres"). The difference is entirely in how tight the
// framing is: this computes camera distance from the point cloud's own XY
// footprint (see topFramingMath.js's own header for why a 3D bounding-
// sphere fit over-frames a straight-down view), using the tightest
// octree-hierarchy-derived extent available with no network/decode/point-
// scan cost - see computeResidentOctreeExtent()'s own doc comment.
//
// Orientation reuses `scene.view`'s existing yaw/pitch model exactly the
// way setTopView() does (yaw 0, "straight down") via View.setView()'s
// own synchronous (duration 0) position+lookAt path - no TWEEN engine
// needed for an instant jump, and no shared code touched: this is a
// parallel, independent call into the same already-existing View API
// every other camera-placement path in this project already uses.

function collectExtentBox(viewer){
	let box = null;

	for(const pc of viewer.scene.pointclouds){
		const geo = pc.pcoGeometry;
		const root = geo && geo.root;
		const resident = computeResidentOctreeExtent(root);

		let worldBox;
		if(resident){
			pc.updateMatrixWorld(true);
			const localBox = new THREE.Box3(
				new THREE.Vector3(resident.min.x, resident.min.y, resident.min.z),
				new THREE.Vector3(resident.max.x, resident.max.y, resident.max.z),
			);
			worldBox = Utils.computeTransformedBoundingBox(localBox, pc.matrixWorld);
		}else{
			// same fallback fitToScreen()/Scene.getBoundingBox() itself already
			// uses (prefers tightBoundingBox, else the padded octree cube) -
			// reused as-is rather than re-implementing that selection here, so
			// this only ever does BETTER than Potree's own Top View, never worse.
			worldBox = viewer.getBoundingBox([pc]);
		}

		box = box ? box.union(worldBox) : worldBox.clone();
	}

	return box;
}

export function installPointViewTopFraming(viewer){
	function trigger(){
		if(!viewer.scene) return;

		const camera = viewer.scene.getActiveCamera();
		// Orthographic zoomTo is an unfinished stub elsewhere in this project
		// too (extensions/OrthographicCamera.js) - matching that existing
		// scope rather than solving ortho framing here.
		if(!(camera instanceof THREE.PerspectiveCamera)) return;

		const box = collectExtentBox(viewer);
		if(!box || box.isEmpty()) return;

		const size = viewer.renderer.getSize(new THREE.Vector2());
		if(size.width <= 0 || size.height <= 0) return;
		const aspect = size.width / size.height;

		const placement = computeTopFramingPlacement(
			{min: {x: box.min.x, y: box.min.y, z: box.min.z}, max: {x: box.max.x, y: box.max.y, z: box.max.z}},
			{aspect, fovDeg: camera.fov},
		);

		const view = viewer.scene.view;
		view.yaw = 0; // stable north-up - same convention setTopView() uses, set independently here
		view.setView(
			[placement.position.x, placement.position.y, placement.position.z],
			[placement.target.x, placement.target.y, placement.target.z],
			0, // synchronous - a keyboard shortcut should jump, not animate
		);
		if(viewer.controls) viewer.controls.stop();
	}

	// Same guard as Digitalisering's "L"/"Shift+L" and the classification
	// "1"-"4" shortcuts (task: "Genbrug den eksisterende keyboard guard") -
	// never fires while the user is typing into an input/textarea/select/
	// contenteditable/numeric popup field anywhere in the app.
	const onKeyDown = (e) => {
		if(e.key.toLowerCase() !== "t" || e.ctrlKey || e.altKey || e.metaKey) return;
		if(shouldIgnoreGlobalShortcut()) return;
		trigger();
	};
	window.addEventListener("keydown", onKeyDown);

	return {
		trigger,
		destroy(){ window.removeEventListener("keydown", onKeyDown); },
	};
}
